import React, { useState } from "react";
import "./change-password.css";
import "./common-view.css"; 
import { useTranslation } from "react-i18next";
import useAuthSession from "../hooks/useAuthSession";
import { BACKEND_BASE_URL } from "../config/appUrls";
import { clearAuthSession } from "../utils/auth";

const EyeIcon = ({ isVisible }) => (
  isVisible ? (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" width="20" height="20">
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  ) : (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" width="20" height="20">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
    </svg>
  )
);

const ChangePassword = () => {
  const { t } = useTranslation();
  const auth = useAuthSession();
  const { token } = auth;

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  
  const [status, setStatus] = useState("");
  const [err, setErr] = useState("");
  
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    
  const onSubmit = async e => {
    e.preventDefault();
    setErr("");
    setStatus("");

    if (newPassword.length < 8) {
      setErr(t('changePassword.errorLength', 'New password must be at least 8 characters.'));
      return;
    }

    if (newPassword !== confirmPassword) {
      setErr(t('changePassword.errorMatch', 'New passwords do not match.'));
      return;
    }

    try {
      if (!token) {
        window.location.href = "/login";
        return;
      }

      const res = await fetch(`${BACKEND_BASE_URL}/api/auth/change-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ currentPassword, newPassword })
      });

      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.error || t('changePassword.errorFailed', "Failed to change password"));

      setStatus(t('changePassword.success', 'Password updated successfully.'));
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setTimeout(() => {
        clearAuthSession();
      }, 1200);
    } catch (e2) {
      setErr(e2.message);
    }
  };

  return (
    <div className="cp-page">
      <form className="cp-card" onSubmit={onSubmit}>
        <h1 className="cp-title">{t('changePassword.title')}</h1>
        <p className="cp-subtitle">
          {t('changePassword.subTitle')}
        </p>

        {err && <div className="cp-error">❌ {err}</div>}
        {status && <div className="cp-success">{status}</div>}

        <label className="cp-label">{t('changePassword.currentPassword')}</label>
        <div className="password-wrapper">
          <input
            className="cp-input"
            type={showCurrentPassword ? "text" : "password"}
            value={currentPassword}
            onChange={e => setCurrentPassword(e.target.value)}
            autoComplete="current-password"
            style={{ width: "100%" }} 
          />
          <button
            type="button"
            className="toggle-password"
            onClick={() => setShowCurrentPassword(v => !v)}
            title={showCurrentPassword ? t('login.hidePassword', 'Hide') : t('login.showPassword', 'Show')}
          >
            <EyeIcon isVisible={showCurrentPassword} />
          </button>
        </div>

        <label className="cp-label">{t('changePassword.newPassword')}</label>
        <div className="password-wrapper">
          <input
            className="cp-input"
            type={showNewPassword ? "text" : "password"}
            value={newPassword}
            onChange={e => setNewPassword(e.target.value)}
            autoComplete="new-password"
            style={{ width: "100%" }}
          />
          <button
            type="button"
            className="toggle-password"
            onClick={() => setShowNewPassword(v => !v)}
            title={showNewPassword ? t('login.hidePassword', 'Hide') : t('login.showPassword', 'Show')}
          >
            <EyeIcon isVisible={showNewPassword} />
          </button>
        </div>

        <label className="cp-label">{t('changePassword.confirmPassword', 'Confirm new password')}</label>
        <div className="password-wrapper">
          <input
            className="cp-input"
            type={showConfirmPassword ? "text" : "password"}
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
            autoComplete="new-password"
            style={{ width: "100%" }}
          />
          <button
            type="button"
            className="toggle-password"
            onClick={() => setShowConfirmPassword(v => !v)}
            title={showConfirmPassword ? t('login.hidePassword', 'Hide') : t('login.showPassword', 'Show')}
          >
            <EyeIcon isVisible={showConfirmPassword} />
          </button>
        </div>

        <div className="cp-actions">
          <button className="btn-cp-primary" type="submit">
            {t('changePassword.savePassword', 'Save new password')}
          </button>

          <a className="btn-cp-cancel" href="/dashboard">
            {t('commonLabel.cancel', 'Cancel')}
          </a>
        </div>
      </form>
    </div>
  );
};

export default ChangePassword;